import { isObject, isArray, hasChanged, isMap, isSet } from '@vue/shared';
import { ChangeDetail } from './Watch';

const enum SyncFlags {
  SYNC_ID = '___sid',
}

const enum TargetType {
  BASIC = 0,
  ADDRESS = 1,
  ARRAY = 2,
  OBJECT = 3,
  SET = 4,
  MAP = 5,
}

export class DataSync {
  // 对象索引
  private map = new Map<string, Object>();

  // 累计值，用于标记对象索引值
  private nextId = 0;

  // 实例标识
  private symbol: string;

  // root的索引ID
  private firstSid: string;

  // gc定时器
  private gcIntervalID: number;

  public constructor(symbol: string) {
    this.symbol = symbol || 'ds';

    this.gcIntervalID = self.setInterval(() => {
      this.gc();
    }, 2000);
  }

  /**
   * 初始化
   * @param value 传入的数据
   * @param isDetail 是否详情，是：一直向下遍历；否：有指针返回指针内容
   * @param keys 构造指定对象key的结构描述
   * @returns description: [type, value, (leafs struct)?]
   */
  public init(value: Object, isDetail: boolean, keys?: string[]): any[] {
    const res = this.structure(value, isDetail,  keys);
    // 需要记录首次的索引ID，作为root节点
    if (!this.firstSid) {
      [,this.firstSid] = res;
    }
    return res;
  }

  /**
   * 解析结构
   * @param description 结构描述
   * @returns 还原后的对象
   */
  public parse(description: any[]) {
    const res = this.unStructure(description);
    // 需要记录首次的索引ID，作为root节点
    if (!this.firstSid) {
      this.firstSid = res[SyncFlags.SYNC_ID];
    }
    return res;
  }

  /**
   * 根据watch的变更详情得到变更补丁
   * @param target 变更对象
   * @param changes 变更操作集合
   * @returns patchs: [target, key, value]
   */
  public organizePatchs(target: any, changes: ChangeDetail[]) {
    const patchs: any[] = [];
    changes.forEach((c) => {
      let base = target;
      const path = c.k;
      const key = path[path.length - 1];
      for (let i = 0; i < path.length - 1; i++) {
        base = base[path[i]];
      }
      patchs.push([base[SyncFlags.SYNC_ID], key, this.structure(c.v, true)]);
    });
    return patchs;
  }

  /**
   * 应用补丁
   * @param patchs 变更补丁
   */
  public executePatchs(patchs: any[]) {
    patchs.forEach((p) => {
      const base: any = this.map.get(p[0]);
      const key = p[1];
      const value = this.unStructure(p[2]);
      base[key] = value;
    });
  }

  /**
   * 对比和应用结构描述
   * @param description 结构描述
   */
  public compareAndPatch(description: any[]) {
    const type = description[0];
    const value = description[1];
    const leafs = description[2];
    // 基本类型直接返回值
    if (type === TargetType.BASIC) {
      return value;
    }

    // 已经索引的对象进行赋值操作
    if (this.map.has(value)) {
      const store: any = this.map.get(value);
      if (type === TargetType.ARRAY) {
        leafs.forEach((v: any, i: number) => {
          const val = this.compareAndPatch(v);
          if (hasChanged(val, store[i])) {
            this.removeFromMap(store[i]);
            store[i] = val;
          }
        });
        // 长度不一致需要裁剪掉
        const extra = store.splice(leafs.length);
        // 裁剪掉的部分需要从索引中移除
        extra.forEach((k: unknown) => this.removeFromMap(k));
      } else {
        const oldKeys = Object.keys(store);
        const newKeys = Object.keys(leafs);
        newKeys.forEach((key) => {
          const val = this.compareAndPatch(leafs[key]);
          // 有变更才赋新值
          if (hasChanged(val, store[key])) {
            this.removeFromMap(store[key]);
            store[key] = val;
          }
        });
        // 从索引中裁剪掉无用的key
        oldKeys.forEach((k: string) => {
          if (!newKeys.includes(k)) {
            this.removeFromMap(store[k]);
          }
        });
      }
      return store;
    }
    let res: any = {};
    // 未索引则赋值后进入索引
    if (type === TargetType.ARRAY) {
      res = leafs.map(((v: any) => this.compareAndPatch(v)));
    } else {
      Object.keys(leafs).forEach((key) => {
        res[key] = this.compareAndPatch(leafs[key]);
      });
    }
    this.setSyncId(res, value);
    return res;
  }

  /**
   * 获取缓存索引长度
   */
  public getMapSize() {
    return this.map.size;
  }

  /**
   * 销毁
   */
  public destroy() {
    self.clearInterval(this.gcIntervalID);
    this.gc();
    this.map.clear();
  }

  /**
   * 构造数据的结构描述
   * @param value 传入的数据
   * @param isDetail 是否详情，是：一直向下遍历；否：有指针返回指针内容
   * @param keys 构造指定对象key的结构描述
   * @returns description: [type, value, (leafs struct)?]
   */
  private structure(value: Object, isDetail: boolean, keys?: string[]): any[] {
    // 基本类型直接返回
    if (!isObject(value)) {
      return [TargetType.BASIC, value];
    }

    let id;
    // 对象有索引从索引取，没有则记入索引
    if (value[SyncFlags.SYNC_ID]) {
      id = value[SyncFlags.SYNC_ID];
      // 根据isDetail判断是否继续
      if (!isDetail) {
        return [TargetType.ADDRESS, id];
      }
    } else {
      id = this.getNextId();
      this.setSyncId(value, id);
    }

    // 数组类型
    if (isArray(value)) {
      return [TargetType.ARRAY, id, value.map(v => this.structure(v, isDetail))];
    }

    // 对象类型
    const obj: any = {};
    Object.keys(value).forEach((key) => {
      if (keys && !keys.includes(key)) {
        return;
      }
      obj[key] = this.structure(value[key], isDetail);
    });
    return [TargetType.OBJECT, id, obj];
  }

  /**
   * 根据structure返回的结构描述，还原对象
   * @param description 结构描述
   * @returns 还原后的对象
   */
  private unStructure(description: any[]) {
    const type = description[0];
    const value = description[1];
    const leafs = description[2];
    // 基本类型直接返回
    if (type === TargetType.BASIC) {
      return value;
    }

    // 从索引中返回
    if (type === TargetType.ADDRESS) {
      return this.map.get(value);
    }

    // 复杂类型
    // 从索引中查找并返回
    if (this.map.has(value)) {
      return this.map.get(value);
    }
    let res: any = {};
    // 向下遍历
    if (type === TargetType.ARRAY) {
      res = leafs.map(((v: any) => this.unStructure(v)));
    } else {
      Object.keys(leafs).forEach((key) => {
        res[key] = this.unStructure(leafs[key]);
      });
    }
    this.setSyncId(res, value);
    return res;
  }

  /**
   * 挂载索引ID并加入索引
   * @param obj 索引对象
   * @param sid 索引ID
   */
  private setSyncId(obj: unknown, sid: string) {
    this.map.set(sid, obj);
    Object.defineProperty(obj, SyncFlags.SYNC_ID, {
      value: sid,
      writable: false,
      enumerable: false,
      configurable: false,
    });
  }

  /**
   * 获取索引ID
   */
  private getNextId() {
    const id = this.nextId;
    this.nextId += 1;
    return `${this.symbol}_${id}`;
  }

  /**
   * 垃圾回收
   */
  private gc() {
    const initialObj = this.map.get(this.firstSid);
    const sidSet = new Set();

    // 遍历查找sid
    const traverse = (value: unknown) => {
      if (!isObject(value)) {
        return;
      }
      // 获取并存储sid
      const sid = value[SyncFlags.SYNC_ID] || 'noop';
      sidSet.add(sid);
      // 继续向下
      if (isArray(value) || isSet(value) || isMap(value)) {
        value.forEach((v: unknown) => {
          traverse(v);
        });
      } else {
        Object.keys(value).forEach((key) => {
          traverse(value[key]);
        });
      }
    };
    traverse(initialObj);

    // 移除不再使用的sid缓存
    this.map.forEach((val, k) => {
      if (!sidSet.has(k)) {
        this.map.delete(k);
      }
    });
  }

  /**
   * 从索引中移除
   * @param value 移除的对象
   */
  private removeFromMap(value: unknown) {
    if (!isObject(value)) {
      return;
    }
    const sid = value[SyncFlags.SYNC_ID] || 'noop';
    this.map.delete(sid);
  }
};
